/**
 * Icons.
 *
 * Inline SVG at 16px on a 1.5 stroke, inheriting `currentColor`. Replaces the Unicode
 * glyphs this console shipped with — `▤ ⚙ ⏻ ◐` render at different weights, baselines and
 * widths on every platform, and Windows is the actual target hardware. docs/03 §12 lists
 * emoji-as-iconography as a tell; a symbol font is the same failure wearing a hat.
 *
 * Icons that encode DIRECTION mirror under RTL; icons that encode an object never do.
 */

export type IconName =
  | "inbox"
  | "channels"
  | "signout"
  | "check"
  | "alert"
  | "search"
  | "send"
  | "clock"
  | "theme"
  | "rows"
  | "chevron";

type Shape = string | { d: string; fill: true };

const PATHS: Record<IconName, Shape | Shape[]> = {
  inbox: "M3 12h4l1.5 2.5h7L17 12h4M3 12l2.5-7h13L21 12v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6Z",
  channels:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z",
  signout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
  check: "M20 6 9 17l-5-5",
  alert:
    "M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3",
  send: "M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2",
  // A circle with one half genuinely filled. A stroked half-arc reads as a stray glyph.
  theme: ["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z", { d: "M12 3a9 9 0 0 1 0 18V3Z", fill: true }],
  rows: "M4 6h16M4 12h16M4 18h16",
  chevron: "m9 18 6-6-6-6",
};

/** Direction-encoding icons mirror; object-encoding icons must not. */
const MIRRORS: ReadonlySet<IconName> = new Set(["signout", "send", "chevron"]);

export function Icon({
  name,
  size = 16,
  label,
}: {
  name: IconName;
  size?: number;
  /** Omit for decorative icons that sit beside a text label. */
  label?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={MIRRORS.has(name) ? "icon icon-mirror" : "icon"}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {(Array.isArray(PATHS[name]) ? PATHS[name] : [PATHS[name]]).map((shape) => {
        const d = typeof shape === "string" ? shape : shape.d;
        return typeof shape === "string" ? (
          <path key={d} d={d} />
        ) : (
          <path key={d} d={d} fill="currentColor" stroke="none" />
        );
      })}
    </svg>
  );
}
